import { InfoCircleFilled } from "@ant-design/icons";
import {
  Alert,
  AnalyticsEvent,
  AnalyticsEventNameType,
  DataImport,
  DataSourceConnectionStatus,
  DataSourceType,
  Writeback,
} from "@coeff/api";
import {
  COLORS,
  ERROR_MESSAGES,
  ERROR_TITLES,
  UNTRUNCATED_SOURCE_ERROR_DATASOURCES,
} from "@coeff/utils";
import { Tooltip } from "antd";
import React, { isValidElement, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useEventManagerContext } from "../globals/contexts";

import { Button } from "./Button";
import { ConfirmBox } from "./ConfirmBox";
import { FlexRow } from "./FlexLayout";
import { Close, SquareCopyIcon } from "./Icons";
import { Typography } from "./Typography";

const Section = styled.section<{ noMargin?: boolean; variant: "error" | "warning" }>`
  margin: ${({ noMargin }) => (noMargin ? 0 : "15px")};
  background: ${({ variant }) => (variant === "error" ? COLORS.redBg : COLORS.orangeBg)};
  border-radius: 5px;
  color: ${COLORS.black85};
  padding: 10px;
  gap: 10px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;

  .header {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;

    .title {
      flex: 1;
      font-weight: bold;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }

  .body {
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    font-weight: 400;
    font-size: 14px;

    ul,
    ol {
      padding-left: 14px;
      margin-bottom: 0;
    }
  }

  .inner-body {
    background-color: ${({ variant }) => (variant === "error" ? "#FFF6F7" : "#FFFDF8")};

    padding: 10px;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    max-height: 310px;
    overflow-y: auto;
    border-radius: 5px;

    .floating-button {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 200px;
      height: 43px;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 50%);
    }

    .source-title {
      font-family: "Proxima Nova", sans-serif;
      font-size: 12px;
      text-transform: uppercase;
    }

    .source-message {
      font-family: "PT Mono", monospace;
      font-size: 12px;
    }
  }

  .action {
    color: ${COLORS.coeblue4};
  }
`;

const Action = styled.a`
  display: flex;
  margin-right: 8px;
`;

const StyledConfirmBox = styled(ConfirmBox)`
  .ant-modal-close-x {
    font-size: 10px;
  }

  .ant-modal-header {
    padding: 20px !important;
  }

  .ant-modal-body {
    padding: 0px 20px 20px !important;

    .message {
      padding-bottom: 10px;
    }

    .hover-button {
      visibility: hidden;
      position: absolute;
      top: 0;
      right: 0;
      width: 100px;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 50%);

      /* antd buttons animate \`all\`, which includes the visibility they inherit from here,
         so on mouse-out the button would linger for the transition after this wrapper (and
         its background) had already hidden. */
      .ant-btn {
        transition-property: color, background-color, border-color, box-shadow;
      }
    }

    &:hover .hover-button {
      visibility: visible;
    }
  }

  .ant-modal-footer {
    display: none !important;
  }
`;

export type ErrorMessageProps = {
  variant?: "error" | "warning";
  isDataSourceReauthError?: boolean;
  dataSourceConnectionStatus?: DataSourceConnectionStatus;
  action?: () => void;
  actionText?: string;
  message?: string | React.ReactNode;
  collapsibleMessage?: boolean;
  messageHtml?: string;
  title?: React.ReactNode;
  style?: object;
  onClose?: () => void;
  dataImport?: DataImport;
  alert?: Alert;
  writeback?: Writeback;
  screen: string;
  noMargin?: boolean;
  secondaryTitle?: string;
  secondaryMessage?: string;
  dataSourceType?: DataSourceType;
  isPreview?: boolean;
  scrollIntoView?: boolean;
  fallbackTrack?: (eventName: AnalyticsEventNameType, eventProps: AnalyticsEvent) => void;
};

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  variant = "error",
  isDataSourceReauthError,
  dataSourceConnectionStatus,
  action,
  actionText,
  message: _message,
  collapsibleMessage,
  messageHtml,
  title,
  onClose,
  dataImport,
  alert,
  writeback,
  noMargin,
  secondaryTitle,
  secondaryMessage,
  dataSourceType,
  screen,
  fallbackTrack,
  isPreview = false,
  scrollIntoView = false,
  ...props
}) => {
  // This can be null for ErrorMessage components that are above App.tsx
  const eventManagerContext = useEventManagerContext() as ReturnType<
    typeof useEventManagerContext
  > | null;

  const track = eventManagerContext?.track ?? fallbackTrack;

  const [isShowFullSecondaryMessage, setIsShowFullSecondaryMessage] = useState(false);

  // Some connectors' source errors are only useful whole (Quiver's carry the generated SQL
  // at the tail). The card still clamps them like any other; the "Show full error" dialog
  // renders them pre-wrapped in a scrolling well rather than growing without bound.
  const isSourceErrorUntruncated =
    !!dataSourceType && UNTRUNCATED_SOURCE_ERROR_DATASOURCES.includes(dataSourceType);

  const [isCopied, setIsCopied] = useState(false);

  const ref = useRef<HTMLElement>(null);

  let message = _message;

  if (!title && !message && !messageHtml) {
    // If neither title or message are specified, use the default fallback title and message
    title = ERROR_TITLES.GENERIC_ERROR;
    message = ERROR_MESSAGES.GENERIC_ERROR;
    messageHtml = undefined;
  }

  if (messageHtml) {
    // If messageHtml is specified, prefer it to the message string
    message = undefined;
  }

  // For regular error objects with a message property,
  // swap title for message to get a better looking error.
  if (!title && message) {
    title = message;
    message = undefined;
  }

  useEffect(() => {
    track?.(
      isDataSourceReauthError
        ? AnalyticsEventNameType.ReconnectBoxShown
        : variant === "warning"
        ? AnalyticsEventNameType.WarningBoxShown
        : AnalyticsEventNameType.RedBoxErrorShown,
      {
        import_data_source:
          dataSourceType ?? dataImport?.data_source_type ?? writeback?.data_source_type,
        data_source: dataSourceType ?? dataImport?.data_source_type ?? writeback?.data_source_type,
        import_id: dataImport?.data_import_id,
        alert_id: alert?.alert_id,
        writeback_id: writeback?.writeback_id,
        screen,
        user_error_message_title: title ? String(title) : undefined,
        user_error_message_body:
          message || messageHtml ? String(message ?? messageHtml) : undefined,
        user_error_message_source_error: secondaryMessage
          ? String(secondaryMessage).slice(0, 2000)
          : undefined,
        is_fallback:
          String(title).toLocaleLowerCase().includes("something went wrong") &&
          String(message).toLocaleLowerCase().includes("send us a message"),
        conn_status: dataSourceConnectionStatus?.status,
        conn_last_checked_dt: dataSourceConnectionStatus?.last_checked_dt,
        conn_data_import_error_type: dataSourceConnectionStatus?.data_import_error_type,
      }
    );
  }, []);

  useEffect(() => {
    if (scrollIntoView && ref.current) {
      ref.current.scrollIntoView?.({ behavior: "smooth", block: "end" });
    }
  }, [scrollIntoView, ref]);

  const copyToClipboard = (secondaryMessage: string) => {
    const tempTextArea = document.createElement("textarea");

    tempTextArea.value = secondaryMessage;
    document.body.appendChild(tempTextArea);

    tempTextArea.select();
    document.execCommand("copy");

    document.body.removeChild(tempTextArea);

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const messageNode = message
    ? typeof message === "string" || isValidElement(message)
      ? message
      : String(message)
    : null;

  return (
    <>
      <Section
        variant={variant}
        data-testid="error-card"
        className="coeff__error-message"
        noMargin={noMargin}
        ref={ref}
        {...props}
      >
        <FlexRow minWidthZero style={{ justifyContent: "space-between" }}>
          <FlexRow
            minWidthZero
            style={{
              flex: 1,
              flexWrap: "wrap",
              rowGap: 8,
              columnGap: 0,
              justifyContent: "space-between",
            }}
          >
            <FlexRow minWidthZero style={{ flexWrap: "wrap", gap: 8 }}>
              <FlexRow className="header" minWidthZero>
                <div className="sectionIcon">
                  <InfoCircleFilled
                    style={{
                      color: variant === "error" ? COLORS.red : COLORS.orange2,
                    }}
                  />
                </div>

                {title && (
                  <Typography className="title" lineClamp={isPreview ? 4 : 8}>
                    {title}
                  </Typography>
                )}
              </FlexRow>

              {messageHtml || message ? (
                <Typography
                  className="body"
                  style={{ marginLeft: 22 }}
                  lineClamp={messageHtml ? undefined : isPreview ? 4 : 8}
                  dangerouslySetInnerHTML={messageHtml ? { __html: messageHtml } : undefined}
                >
                  {messageNode}
                </Typography>
              ) : null}
            </FlexRow>

            {action && actionText && (
              <Action
                onClick={() => action()}
                className="action"
                style={{ marginLeft: 22, marginRight: 8, fontWeight: 700 }}
              >
                {actionText}
              </Action>
            )}
          </FlexRow>

          {onClose && (
            <Button
              type="text"
              noPadding
              style={{ height: "min-content", alignSelf: "flex-start", marginTop: 2 }}
              icon={<Close height="12" width="12" />}
              onClick={onClose}
            />
          )}
        </FlexRow>

        {secondaryMessage && (
          <FlexRow
            className="inner-body"
            style={{
              position: "relative",
              flexWrap: "wrap",
              rowGap: 8,
              columnGap: 8,
              justifyContent: "space-between",
              flexDirection: isPreview ? "row" : "column",
            }}
          >
            <FlexRow
              minWidthZero
              style={{
                flexWrap: "wrap",
                rowGap: 4,
                columnGap: 22,
                flex: 1,
              }}
            >
              {secondaryTitle || dataSourceType ? (
                <FlexRow className="source-title" style={{ fontWeight: "bold", maxWidth: "100%" }}>
                  {secondaryTitle || `ERROR FROM ${dataSourceType?.toUpperCase()}`}
                </FlexRow>
              ) : null}

              <Typography
                id="source-message"
                className="source-message"
                lineClamp={isPreview ? 1 : 3}
                style={isPreview ? { flex: "1 1 0%" } : undefined}
              >
                {secondaryMessage}
              </Typography>
            </FlexRow>

            <div className={isPreview ? undefined : "floating-button"}>
              <Button
                size="small"
                type="default"
                onClick={() => setIsShowFullSecondaryMessage(true)}
                style={
                  isPreview
                    ? undefined
                    : { height: "min-content", bottom: 10, right: 10, position: "absolute" }
                }
              >
                Show full error
              </Button>
            </div>
          </FlexRow>
        )}
      </Section>

      {isShowFullSecondaryMessage && (
        <StyledConfirmBox
          closable={true}
          title={
            <FlexRow>
              <InfoCircleFilled
                style={{
                  fontSize: 14,
                  color: variant === "error" ? COLORS.red : COLORS.orange2,
                }}
              />
              <Typography fontSize={16}>{title ?? "Error details"}</Typography>
            </FlexRow>
          }
          visible={isShowFullSecondaryMessage}
          maskClosable={true}
          onCancel={() => setIsShowFullSecondaryMessage(false)}
          showOk={false}
          showCancel={false}
          width={isPreview ? 600 : undefined}
        >
          {messageHtml || message ? (
            <Typography
              className="body"
              style={{ marginLeft: 22 }}
              lineClamp={messageHtml ? undefined : isPreview ? 4 : 8}
              dangerouslySetInnerHTML={messageHtml ? { __html: messageHtml } : undefined}
            >
              {messageNode}
            </Typography>
          ) : null}

          <div style={{ position: "relative" }}>
            {secondaryTitle || dataSourceType ? (
              <Typography fontSize={12} fontWeight="bold" uppercase>
                {secondaryTitle || `ERROR FROM ${dataSourceType}`}
              </Typography>
            ) : null}

            {/* When the text scrolls, the copy button anchors to the text field's own top
                right (and clears its scrollbar) rather than to the title line above it. */}
            <div style={isSourceErrorUntruncated ? { position: "relative" } : undefined}>
              <div
                style={{
                  fontFamily: "PT Mono, monospace",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  padding: "5px 0",
                  ...(isSourceErrorUntruncated
                    ? {
                        whiteSpace: "pre-wrap" as const,
                        maxHeight: "60vh",
                        overflowY: "auto" as const,
                      }
                    : undefined),
                }}
              >
                {secondaryMessage}
              </div>

              <div
                className="hover-button"
                style={{
                  height: isPreview ? 24 : 40,
                  // The shared style is a 100px white gradient that fades text under the
                  // button. Over a scrolling well that width lands on the scrollbar, so the
                  // button carries its own solid background instead (see below), and this
                  // wrapper only places it, inset past the scrollbar.
                  ...(isSourceErrorUntruncated
                    ? { right: 20, width: "auto", height: "auto", background: "none" }
                    : undefined),
                }}
              >
                <Tooltip title={isCopied ? "Copied!" : "Copy"}>
                  <Button
                    type="text"
                    size="small"
                    className="inner-body-icon"
                    style={
                      isSourceErrorUntruncated
                        ? // One element masks the text beneath the icon and takes the click,
                          // so its background cannot show or hide apart from the icon. Flex
                          // with no padding centers the icon; antd's small-button padding
                          // and block layout would leave it in the top left.
                          {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            width: 32,
                            height: 28,
                            background: "rgba(255, 255, 255, 0.8)",
                            borderRadius: 4,
                          }
                        : { height: "min-content", top: 0, right: 0, position: "absolute" }
                    }
                    onClick={() => {
                      track?.(AnalyticsEventNameType.CopySourceErrorClicked, {
                        conn_data_source: dataSourceType,
                        contains_friendly_error_message: Boolean(message || messageHtml),
                      });

                      copyToClipboard(secondaryMessage ?? "");
                    }}
                  >
                    <SquareCopyIcon />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </StyledConfirmBox>
      )}
    </>
  );
};
